import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

/** Six digits, by touch or by the PC keyboard (the workshop PC). The value lives in the parent; the pad never stores it. */
export function PinPad({ value, onChange, length = 6, disabled = false, label }: { value: string; onChange: (v: string) => void; length?: number; disabled?: boolean; label: string }) {
  const { t } = useTranslation()
  useEffect(() => {
    if (disabled) return
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (/^\d$/.test(e.key)) onChange((value + e.key).slice(0, length))
      else if (e.key === 'Backspace') onChange(value.slice(0, -1))
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [value, onChange, length, disabled])
  const key = (d: string) => (
    <button key={d} type="button" className="btn !min-h-16 text-2xl" disabled={disabled || value.length >= length} onClick={() => onChange(value + d)} data-testid={`pin-key-${d}`}>{d}</button>
  )
  return (
    <div className="mx-auto flex w-full max-w-xs flex-col gap-4" role="group" aria-label={label}>
      <div className="flex justify-center gap-3" data-testid="pin-dots" aria-live="polite" aria-label={t('kiosk.digitsEntered', { count: value.length })}>
        {Array.from({ length }, (_, i) => <span key={i} className={`size-5 rounded-full border-2 ${i < value.length ? 'border-brand bg-brand' : 'border-slate-400'}`} />)}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(key)}
        <button type="button" className="btn !min-h-16" disabled={disabled || value.length === 0} onClick={() => onChange('')} data-testid="pin-clear">{t('kiosk.clear')}</button>
        {key('0')}
        <button type="button" className="btn !min-h-16 text-2xl" disabled={disabled || value.length === 0} onClick={() => onChange(value.slice(0, -1))} aria-label={t('kiosk.erase')} data-testid="pin-back">⌫</button>
      </div>
    </div>
  )
}
